import React, { useState, useEffect, useRef, Fragment } from "react";
import api from "../../config/axios";
import { Menu, Transition } from "@headlessui/react";
import {
  DocumentChartBarIcon,
  ArrowDownTrayIcon,
  EllipsisVerticalIcon,
} from "@heroicons/react/24/outline";
import TaxReceiptModal from "./TaxReceiptModal";
import ReportModal from "./ReportModal";
import { generatePDF, generateDOC } from "../../services/reportService";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const formatCurrency = (value) => {
  return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

const TaxReport = () => {
  const [notasFiscais, setNotasFiscais] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtroStatus, setFiltroStatus] = useState("TODOS");
  const [filtroDataInicial, setFiltroDataInicial] = useState("");
  const [filtroDataFinal, setFiltroDataFinal] = useState("");
  const [filtroEmpresa, setFiltroEmpresa] = useState("");
  const [empresaDebounced, setEmpresaDebounced] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totals, setTotals] = useState({
    valorNota: 0,
    valorRetencaoIRRF: 0,
    valorRetencaoISS: 0,
    liquidoApagar: 0,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isCnpjModalOpen, setIsCnpjModalOpen] = useState(false);
  const [selectedNota, setSelectedNota] = useState(null);
  const [selectedNotas, setSelectedNotas] = useState([]);
  const [cnpjFormData, setCnpjFormData] = useState({ cnpj: "" });
  const prevFiltersRef = useRef({
    filtroStatus,
    filtroDataInicial,
    filtroDataFinal,
    empresaDebounced,
  });

  useEffect(() => {
    const timer = setTimeout(() => setEmpresaDebounced(filtroEmpresa), 500);
    return () => clearTimeout(timer);
  }, [filtroEmpresa]);

  const carregarNotasFiscais = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get("/notas-fiscais", {
        params: {
          page,
          limit,
          status: filtroStatus,
          dataInicial: filtroDataInicial || undefined,
          dataFinal: filtroDataFinal || undefined,
          empresa: empresaDebounced || undefined,
        },
      });

      if (!response.data) {
        throw new Error("Dados não recebidos da API");
      }

      const dados = Array.isArray(response.data.data) ? response.data.data : [];

      const notasFormatadas = dados.map((nota) => ({
        ...nota,
        valorNota: Number(nota.valorNota || 0),
        aliqRetencao: Number(nota.aliqRetencao || 0),
        valorRetencaoIRRF: Number(nota.valorRetencaoIRRF || 0),
        valorRetencaoISS: Number(nota.valorRetencaoISS || 0),
        liquidoApagar: Number(nota.liquidoApagar || 0),
      }));

      setNotasFiscais(notasFormatadas);
      setTotal(response.data.meta?.total ?? notasFormatadas.length);
      setTotalPages(response.data.meta?.totalPages ?? 1);
      setTotals({
        valorNota: Number(response.data.totals?.valorNota || 0),
        valorRetencaoIRRF: Number(response.data.totals?.valorRetencaoIRRF || 0),
        valorRetencaoISS: Number(response.data.totals?.valorRetencaoISS || 0),
        liquidoApagar: Number(response.data.totals?.liquidoApagar || 0),
      });
    } catch (error) {
      setError(
        error.response?.data?.message || "Erro ao carregar notas fiscais"
      );
      toast.error(
        error.response?.data?.message || "Erro ao carregar notas fiscais"
      );

      if (error.response?.status === 401) {
        window.location.href = "/login";
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const filtersChanged =
      prevFiltersRef.current.filtroStatus !== filtroStatus ||
      prevFiltersRef.current.filtroDataInicial !== filtroDataInicial ||
      prevFiltersRef.current.filtroDataFinal !== filtroDataFinal ||
      prevFiltersRef.current.empresaDebounced !== empresaDebounced;

    if (filtersChanged) {
      prevFiltersRef.current = {
        filtroStatus,
        filtroDataInicial,
        filtroDataFinal,
        empresaDebounced,
      };

      if (page !== 1) {
        setPage(1);
        return;
      }
    }

    carregarNotasFiscais();
  }, [page, filtroStatus, filtroDataInicial, filtroDataFinal, empresaDebounced]);

  const handleLimparFiltros = () => {
    setFiltroStatus("TODOS");
    setFiltroDataInicial("");
    setFiltroDataFinal("");
    setFiltroEmpresa("");
    setSelectedNotas([]);
  };

  const startItem = total === 0 ? 0 : (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, total);
  const currentPageIds = notasFiscais.map((nota) => nota.id);
  const allCurrentPageSelected =
    currentPageIds.length > 0 &&
    currentPageIds.every((id) => selectedNotas.some((nota) => nota.id === id));

  const handleGerarRelatorio = () => {
    if (selectedNotas.length === 0) {
      toast.warning(
        "Selecione pelo menos uma nota fiscal para gerar o relatório"
      );
      return;
    }
    setIsReportModalOpen(true);
  };

  const handleSelectItem = (nota) => {
    setSelectedNotas((prev) => {
      if (prev.some((item) => item.id === nota.id)) {
        return prev.filter((item) => item.id !== nota.id);
      }
      return [...prev, nota];
    });
  };

  const handleSelectAll = () => {
    if (allCurrentPageSelected) {
      setSelectedNotas((prev) =>
        prev.filter((nota) => !currentPageIds.includes(nota.id))
      );
      return;
    }

    setSelectedNotas((prev) => {
      const existingIds = new Set(prev.map((nota) => nota.id));
      const toAdd = notasFiscais.filter((nota) => !existingIds.has(nota.id));
      return [...prev, ...toAdd];
    });
  };

  const handleExport = (format) => {
    const selectedTotalIRRF = selectedNotas.reduce(
      (total, nota) => total + nota.valorRetencaoIRRF,
      0
    );
    const selectedTotalISS = selectedNotas.reduce(
      (total, nota) => total + nota.valorRetencaoISS,
      0
    );
    const selectedTotalNotas = selectedNotas.reduce(
      (total, nota) => total + nota.valorNota,
      0
    );
    const selectedTotalLiquido = selectedNotas.reduce(
      (total, nota) => total + nota.liquidoApagar,
      0
    );

    const reportData = {
      notasFiltradas: selectedNotas,
      totalIRRF: selectedTotalIRRF,
      totalISS: selectedTotalISS,
      totalNotas: selectedTotalNotas,
      totalLiquido: selectedTotalLiquido,
      filtroDataInicial,
      filtroDataFinal,
      filtroStatus,
      filtroEmpresa,
    };

    try {
      if (format === "pdf") {
        generatePDF(reportData);
      } else if (format === "doc") {
        generateDOC(reportData);
      }
      toast.success(
        `Relatório exportado com sucesso como ${format.toUpperCase()}`
      );
    } catch (error) {
      toast.error("Erro ao exportar relatório");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleCancelarNota = async (notaId) => {
    if (!window.confirm("Tem certeza que deseja cancelar esta nota fiscal?")) {
      return;
    }
    try {
      await api.patch(`/notas-fiscais/${notaId}/cancelar`);

      // Atualiza a lista local
      setNotasFiscais((prev) =>
        prev.map((nota) =>
          nota.id === notaId ? { ...nota, status: "CANCELADA" } : nota
        )
      );
      toast.success("Nota fiscal cancelada com sucesso!");
    } catch (error) {
      toast.error("Erro ao cancelar nota fiscal. Tente novamente.");
    }
  };

  const handleReceberImposto = async (notaId) => {
    setSelectedNota(notasFiscais.find((nota) => nota.id === notaId));
    setIsModalOpen(true);
  };

  const handleCnpjClick = (nota) => {
    setSelectedNota(nota);
    setCnpjFormData({ cnpj: nota.fornecedorCnpj || "" });
    setIsCnpjModalOpen(true);
  };

  const handleCnpjSubmit = async (e) => {
    e.preventDefault();
    try {
      // Usar endpoint genérico que deve existir no servidor
      await api.patch(`/notas-fiscais/${selectedNota.id}`, {
        fornecedorCnpj: cnpjFormData.cnpj,
      });

      // Atualizar a lista local
      setNotasFiscais((prev) =>
        prev.map((nota) =>
          nota.id === selectedNota.id
            ? { ...nota, fornecedorCnpj: cnpjFormData.cnpj }
            : nota
        )
      );

      toast.success("CNPJ atualizado com sucesso!");
      setIsCnpjModalOpen(false);
    } catch (error) {
      toast.error("Erro ao atualizar CNPJ. Tente novamente.");
    }
  };

  const handleConfirmRecebimento = async (formData) => {
    try {
      // Enviar dados para o backend
      await api.post(`/notas-fiscais/${selectedNota.id}/receber`, {
        dataRecebimento: formData.dataRecebimento,
        observacoes: formData.observacoes,
      });

      // Fechar o modal e recarregar os dados
      setIsModalOpen(false);
      setSelectedNota(null);
      await carregarNotasFiscais();

      toast.success("Imposto recebido com sucesso!");
    } catch (error) {
      toast.error("Erro ao receber imposto. Tente novamente.");
    }
  };

  const handleDownloadPDF = async (nota) => {
    try {
      // Usar a rota de download com o ID da nota
      const response = await api.get(`notas-fiscais/${nota.id}/download`, {
        responseType: "blob",
        headers: {
          Accept: "*/*",
        },
      });

      const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/octet-stream",
      });
      const url = window.URL.createObjectURL(blob);

      // Extrair a extensão do arquivo original
      const extensao = nota.arquivoImagem.split(".").pop();

      // Criar link temporário para download
      const link = document.createElement("a");
      link.href = url;
      link.download = `nota-fiscal-${nota.numeroNota}.${extensao}`; // Mantém a extensão original
      document.body.appendChild(link);
      link.click();

      // Remover link e revogar URL
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Download iniciado com sucesso");
    } catch (error) {
      console.error("Erro ao fazer download:", error);
      toast.error(
        "Erro ao fazer download do arquivo. Por favor, tente novamente."
      );
    }
  };

  if (error) return <div className="text-center text-red-600 p-4">{error}</div>;

  return (
    <div className="p-6 bg-slate-900 rounded-lg shadow-sm border border-slate-800">
      <ToastContainer />
      <div className="space-y-6">
        {/* Filtros */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-300">
              Data Inicial
            </label>
            <input
              type="date"
              value={filtroDataInicial}
              onChange={(e) => setFiltroDataInicial(e.target.value)}
              className="block w-full px-3 py-2 border border-slate-600 rounded-md shadow-sm bg-slate-800 text-white focus:ring-indigo-500 focus:border-indigo-500 [color-scheme:dark]"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-300">
              Data Final
            </label>
            <input
              type="date"
              value={filtroDataFinal}
              onChange={(e) => setFiltroDataFinal(e.target.value)}
              className="block w-full px-3 py-2 border border-slate-600 rounded-md shadow-sm bg-slate-800 text-white focus:ring-indigo-500 focus:border-indigo-500 [color-scheme:dark]"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-300">
              Empresa
            </label>
            <input
              type="text"
              value={filtroEmpresa}
              onChange={(e) => setFiltroEmpresa(e.target.value)}
              placeholder="Digite o nome da empresa"
              className="block w-full px-3 py-2 border border-slate-600 rounded-md shadow-sm bg-slate-800 text-white focus:ring-indigo-500 focus:border-indigo-500 placeholder-slate-500"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-300">
              Status
            </label>
            <select
              value={filtroStatus}
              onChange={(e) => setFiltroStatus(e.target.value)}
              className="block w-full px-3 py-2 border border-slate-600 rounded-md shadow-sm bg-slate-800 text-white focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="TODOS">Todos</option>
              <option value="PENDENTE">Pendente</option>
              <option value="RECEBIDO">Recebido</option>
              <option value="CANCELADA">Cancelada</option>
            </select>
          </div>
          <div className="space-y-2 flex items-end">
            <button
              onClick={handleLimparFiltros}
              className="w-full px-4 py-2 text-sm font-medium text-slate-300 bg-slate-800 border border-slate-600 rounded-md shadow-sm hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-indigo-500 transition-colors"
            >
              Limpar Filtros
            </button>
          </div>
        </div>

        {/* Resultados da Filtragem */}
        <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between text-sm text-slate-400">
          <div>
            {total}{" "}
            {total === 1 ? "nota fiscal encontrada" : "notas fiscais encontradas"}
            {total > 0 && (
              <span className="ml-2">
                (exibindo {startItem}-{endItem})
              </span>
            )}
            {selectedNotas.length > 0 && (
              <span className="ml-2 text-indigo-400">
                {selectedNotas.length} selecionada
                {selectedNotas.length === 1 ? "" : "s"}
              </span>
            )}
          </div>

          <button
            onClick={handleGerarRelatorio}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-indigo-500"
          >
            <DocumentChartBarIcon className="h-5 w-5 mr-2" />
            Gerar Relatório
          </button>
        </div>

        {/* Tabela de Resultados */}
        <div className="mt-4 w-full overflow-x-auto rounded-lg border border-slate-700 relative">
          {loading && (
            <div className="absolute inset-0 bg-slate-900/60 flex items-center justify-center z-10">
              <span className="text-slate-300 text-sm">Carregando...</span>
            </div>
          )}
          <table className="min-w-full table-fixed divide-y divide-slate-700 text-xs">
            <thead className="bg-slate-800">
              <tr>
                <th className="w-[2%] px-2 py-2 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={allCurrentPageSelected}
                    onChange={handleSelectAll}
                    className="h-4 w-4 text-indigo-500 focus:ring-indigo-500 border-slate-600 bg-slate-700 rounded"
                  />
                </th>
                <th className="w-[17%] px-2 py-2 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Fonte Pagadora
                </th>
                <th className="w-[26%] px-2 py-2 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Fornecedor
                </th>
                <th className="w-[7%] px-2 py-2 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Data Emissão
                </th>
                <th className="w-[6%] px-2 py-2 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Nº Nota
                </th>
                <th className="w-[8%] px-2 py-2 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Valor Nota
                </th>
                <th className="w-[7%] px-2 py-2 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  IRRF
                </th>
                <th className="w-[6%] px-2 py-2 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  ISS
                </th>
                <th className="w-[8%] px-2 py-2 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Líquido
                </th>
                <th className="w-[7%] px-2 py-2 text-center text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="w-[6%] px-2 py-2 text-center text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-slate-900 divide-y divide-slate-700">
              {notasFiscais.length === 0 && !loading ? (
                <tr>
                  <td colSpan="11" className="px-4 py-8 text-center text-slate-400">
                    Nenhuma nota fiscal encontrada com os filtros aplicados.
                  </td>
                </tr>
              ) : (
              notasFiscais.map((nota) => (
                <tr key={nota.id} className="hover:bg-slate-800 transition-colors">
                  <td className="px-2 py-2 text-xs">
                    <input
                      type="checkbox"
                      checked={selectedNotas.some((item) => item.id === nota.id)}
                      onChange={() => handleSelectItem(nota)}
                      className="h-4 w-4 text-indigo-500 focus:ring-indigo-500 border-slate-600 bg-slate-700 rounded"
                    />
                  </td>
                  <td className="px-2 py-2 text-xs text-slate-300 break-words">
                    <div className="font-medium">{nota.payerSource?.nomeFonte || '-'}</div>
                  </td>
                  <td className="px-2 py-2 text-xs text-slate-300 break-words">
                    <div>
                      <div className="font-medium">{nota.fornecedorNome}</div>
                      {nota.fornecedorCnpj && (
                        <div className="text-slate-500 text-xs mt-1">
                          {nota.fornecedorCnpj.replace(
                            /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
                            "$1.$2.$3/$4-$5"
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-2 text-xs text-slate-300">
                    {new Date(nota.dataEmissao).toLocaleDateString()}
                  </td>
                  <td className="px-2 py-2 text-xs text-slate-300">
                    {nota.numeroNota}
                  </td>
                  <td className="px-2 py-2 text-xs text-slate-300">
                    {formatCurrency(nota.valorNota)}
                  </td>
                  <td className="px-2 py-2 text-xs text-slate-300">
                    {formatCurrency(nota.valorRetencaoIRRF)}
                  </td>
                  <td className="px-2 py-2 text-xs text-slate-300">
                    {formatCurrency(nota.valorRetencaoISS)}
                  </td>
                  <td className="px-2 py-2 text-xs text-slate-300">
                    {formatCurrency(nota.liquidoApagar)}
                  </td>
                  <td className="px-2 py-2 text-center text-xs">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        nota.status === "RECEBIDO"
                          ? "bg-green-900/50 text-green-300 border border-green-800"
                          : nota.status === "CANCELADA"
                          ? "bg-red-900/50 text-red-300 border border-red-800"
                          : "bg-yellow-900/50 text-yellow-300 border border-yellow-800"
                      }`}
                    >
                      {nota.status}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-center text-xs">
                    <Menu as="div" className="relative inline-block text-left">
                      <div>
                        <Menu.Button className="inline-flex justify-center w-full rounded-md px-2 py-1 text-sm font-medium text-slate-300 hover:bg-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75">
                          <EllipsisVerticalIcon
                            className="h-5 w-5 text-slate-400 hover:text-slate-200"
                            aria-hidden="true"
                          />
                        </Menu.Button>
                      </div>
                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                      >
                        <Menu.Items className="absolute right-0 mt-2 w-40 origin-top-right divide-y divide-slate-700 rounded-md bg-slate-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                          <div className="px-1 py-1">
                            <Menu.Item>
                              {({ active }) => (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCnpjClick(nota);
                                  }}
                                  className={`${
                                    active
                                      ? "bg-slate-700 text-white"
                                      : "text-slate-300"
                                  } group flex w-full items-center rounded-md px-2 py-2 text-xs`}
                                >
                                  Atualizar CNPJ
                                </button>
                              )}
                            </Menu.Item>
                            {nota.status === "PENDENTE" && (
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleReceberImposto(nota.id);
                                    }}
                                    className={`${
                                      active
                                        ? "bg-indigo-600 text-white"
                                        : "text-slate-300"
                                    } group flex w-full items-center rounded-md px-2 py-2 text-xs`}
                                  >
                                    Receber
                                  </button>
                                )}
                              </Menu.Item>
                            )}
                            {nota.status !== "CANCELADA" && (
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCancelarNota(nota.id);
                                    }}
                                    className={`${
                                      active
                                        ? "bg-red-900/50 text-red-300"
                                        : "text-red-400"
                                    } group flex w-full items-center rounded-md px-2 py-2 text-xs`}
                                  >
                                    Cancelar
                                  </button>
                                )}
                              </Menu.Item>
                            )}
                          </div>
                        </Menu.Items>
                      </Transition>
                    </Menu>
                  </td>
                </tr>
              ))
              )}
            </tbody>
            {/* Linha de Totais */}
            <tfoot className="bg-slate-800 font-semibold">
              <tr>
                <td className="px-2 py-3 text-xs text-slate-300" colSpan="5">
                  Total Geral (filtrado)
                </td>
                <td className="px-2 py-3 text-xs text-slate-300">
                  {formatCurrency(totals.valorNota)}
                </td>
                <td className="px-2 py-3 text-xs text-slate-300">
                  {formatCurrency(totals.valorRetencaoIRRF)}
                </td>
                <td className="px-2 py-3 text-xs text-slate-300">
                  {formatCurrency(totals.valorRetencaoISS)}
                </td>
                <td className="px-2 py-3 text-xs text-slate-300">
                  {formatCurrency(totals.liquidoApagar)}
                </td>
                <td colSpan="2"></td>
              </tr>
            </tfoot>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pt-4">
            <div className="text-sm text-slate-400">
              Página {page} de {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((current) => Math.max(current - 1, 1))}
                disabled={page === 1 || loading}
                className="px-3 py-2 text-sm font-medium text-slate-300 bg-slate-800 border border-slate-600 rounded-md hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anterior
              </button>
              <button
                onClick={() => setPage((current) => Math.min(current + 1, totalPages))}
                disabled={page === totalPages || loading}
                className="px-3 py-2 text-sm font-medium text-slate-300 bg-slate-800 border border-slate-600 rounded-md hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Próxima
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Recebimento */}
      <TaxReceiptModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        nota={selectedNota}
        onConfirm={handleConfirmRecebimento}
      />

      {/* Modal de Relatório */}
      <ReportModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        data={{
          notasFiltradas: selectedNotas,
          totalIRRF: selectedNotas.reduce(
            (total, nota) => total + nota.valorRetencaoIRRF,
            0
          ),
          totalISS: selectedNotas.reduce(
            (total, nota) => total + nota.valorRetencaoISS,
            0
          ),
          totalNotas: selectedNotas.reduce(
            (total, nota) => total + nota.valorNota,
            0
          ),
          totalLiquido: selectedNotas.reduce(
            (total, nota) => total + nota.liquidoApagar,
            0
          ),
          filtroDataInicial,
          filtroDataFinal,
          filtroStatus,
          filtroEmpresa,
        }}
        onExport={handleExport}
        onPrint={handlePrint}
      />

      {/* Modal de Visualização de Nota */}
      <ReportModal
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setSelectedNota(null);
        }}
        data={selectedNota}
      />

      {/* Modal de CNPJ */}
      {isCnpjModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-slate-900 rounded-lg p-6 w-96 max-w-md mx-4 border border-slate-800 shadow-xl">
            <h3 className="text-lg font-medium text-white mb-4">
              Atualizar CNPJ - {selectedNota?.fornecedorNome}
            </h3>
            <form onSubmit={handleCnpjSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  CNPJ do Fornecedor
                </label>
                <input
                  type="text"
                  value={cnpjFormData.cnpj}
                  onChange={(e) => setCnpjFormData({ cnpj: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-600 bg-slate-800 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 placeholder-slate-500"
                  placeholder="00.000.000/0000-00"
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsCnpjModalOpen(false)}
                  className="px-4 py-2 text-slate-300 bg-slate-800 border border-slate-600 rounded-md hover:bg-slate-700 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-indigo-500"
                >
                  Salvar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaxReport;
